import { Grid, Snackbar, IconButton } from '@mui/material';
import React, { useState } from 'react';
import { Options } from '../Options';
import download from 'shared/Download';
import { IBusinessJobDetailsModel, ILeanShift } from '../../BusinessJob/models';
import CancelModal from './CancelModal';
import { startOfTomorrow, isAfter, isSameDay } from 'date-fns';
import EditModal from './EditModal';
import { Download, Edit, EventBusy, ReadMore, Star, Close, Visibility } from '@mui/icons-material';
import { useUserContext } from 'UserContext';
import RevealModal from './RevealModal';
import { client } from 'shared';
import { IShift, ShiftJob, ShiftProviderUser } from 'models';

export default function ShiftCardMenu({
    provider_id,
    shift,
    ongoing_request,
    provider,
    onClose,
    status,
    refresh,
}: {
    provider_id?: number;
    shift: IShift | ILeanShift;
    ongoing_request: IBusinessJobDetailsModel | ShiftJob;
    provider?: ShiftProviderUser;
    onClose?: () => void;
    status: string;
    refresh: () => void;
}) {
    const [snackOpen, setSnackOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [ssnInputOpen, setSsnInputOpen] = useState(false);

    const { user } = useUserContext();

    const isOpenOrFilledFutureShift =
        ['UNFILLED', 'ON_STAFF'].includes(status) && isAfter(new Date(shift.start_date), startOfTomorrow());

    const handleClose = () => {
        setSnackOpen(false);
    };

    const onDownload = async (providerId: number) => {
        try {
            setSnackOpen(true);
            await download(`api/providers/${providerId}/documents/`);
        } catch (error) {
            alert('No documents found');
        }
    };

    function showSSNReveal() {
        let showSSN = false;
        user?.address?.forEach((address) => {
            if (address.state === 'Florida') {
                showSSN = true;
            }
        });
        return showSSN;
    }

    const onSendCode = async () => {
        try {
            setSsnInputOpen(true);
            client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider?.id } });
        } catch (error) {
            alert('Cannot send SSN reveal code');
        }
    };

    const documentOption = {
        text: 'Download documents',
        onClick: () => {
            if (provider_id) onDownload(provider_id);
        },
        icon: <Download />,
    };
    const detailOption = {
        text: 'Job details',
        to: `/jobs/${ongoing_request.id}`,
        icon: <ReadMore />,
    };

    const editOption = {
        text: 'Edit times',
        onClick: () => setEditOpen(true),
        icon: <Edit />,
    };
    const cancelOption = {
        text: 'Cancel this shift',
        onClick: () => setCancelOpen(true),
        icon: <EventBusy />,
    };
    const reviewOption = { text: 'Provide feedback', to: `/providers/review/${provider?.id}`, icon: <Star /> };
    const ssnOption = {
        text: 'Reveal SSN',
        onClick: () => onSendCode(),
        icon: <Visibility />,
    };

    const menuOptions = [
        { option: documentOption, show: !!provider_id },
        { option: detailOption, show: true },
        { option: editOption, show: isOpenOrFilledFutureShift },
        {
            option: cancelOption,
            show:
                (isOpenOrFilledFutureShift || isSameDay(new Date(shift.start_date), new Date())) &&
                !shift.hour_reports.length,
        },
        { option: reviewOption, show: !!provider && status === 'COMPLETED' },
        { option: ssnOption, show: !!provider_id && showSSNReveal() },
    ];

    return (
        <Grid item xs={1}>
            <Options
                options={menuOptions.filter((option) => option.show).map((option) => option.option)}
                onClose={onClose}
            />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={snackOpen}
                autoHideDuration={6000}
                onClose={handleClose}
                message="Download Started"
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
            <CancelModal
                open={cancelOpen}
                close={() => setCancelOpen(false)}
                shift={shift}
                providerName={provider?.first_name}
                refresh={refresh}
            />
            <EditModal
                open={editOpen}
                close={() => setEditOpen(false)}
                shift={shift}
                providerName={provider?.first_name}
                refresh={refresh}
            />
            <RevealModal
                open={ssnInputOpen}
                close={() => setSsnInputOpen(false)}
                providerID={provider?.id}
                providerName={provider?.first_name}
                refresh={refresh}
            />
        </Grid>
    );
}
